import type {
  AutomationRule,
  BoardPayload,
  Comment,
  CustomFieldDefinition,
  Issue,
  IssueHistory,
  Project,
  Sprint,
  UserSummary,
  WorkflowStatus
} from "@pms/shared";

export interface AuthState {
  accessToken: string;
  user: UserSummary & { orgRole: string };
}

let token = localStorage.getItem("accessToken") ?? "";

export function setAccessToken(next: string): void {
  token = next;
  if (next) localStorage.setItem("accessToken", next);
  else localStorage.removeItem("accessToken");
}

export function getAccessToken(): string {
  return token;
}

async function request<T>(path: string, options: RequestInit = {}, allowRetry = true): Promise<T> {
  const headers = new Headers(options.headers);
  if (!(options.body instanceof FormData)) headers.set("content-type", "application/json");
  if (token) headers.set("authorization", `Bearer ${token}`);
  const response = await fetch(path, {
    ...options,
    headers,
    credentials: "include"
  });
  if (response.status === 401 && allowRetry && path !== "/api/auth/refresh") {
    const refreshed = await refresh().catch(() => null);
    if (refreshed) return request<T>(path, options, false);
  }
  if (!response.ok) {
    const payload = await response.json().catch(() => ({}));
    throw new Error(payload.error ?? `Request failed: ${response.status}`);
  }
  return response.json() as Promise<T>;
}

export const api = {
  login: (email: string, password: string) =>
    request<AuthState>("/api/auth/login", {
      method: "POST",
      body: JSON.stringify({ email, password })
    }).then((result) => {
      setAccessToken(result.accessToken);
      return result;
    }),
  refresh,
  logout: () =>
    request<{ ok: boolean }>("/api/auth/logout", { method: "POST" }).finally(() => setAccessToken("")),
  me: () => request<{ user: AuthState["user"] }>("/api/auth/me"),
  projects: () => request<{ projects: Project[] }>("/api/projects"),
  createProject: (payload: Partial<Project> & { key: string; name: string }) =>
    request<{ project: Project }>("/api/projects", { method: "POST", body: JSON.stringify(payload) }),
  project: (key: string) =>
    request<{ project: Project; statuses: WorkflowStatus[]; users: UserSummary[] }>(`/api/projects/${key}`),
  workflow: (key: string) =>
    request<{ statuses: WorkflowStatus[]; transitions: Array<Record<string, unknown>> }>(`/api/projects/${key}/workflow`),
  saveWorkflowStatuses: (key: string, statuses: Array<Partial<WorkflowStatus>>) =>
    request<{ statuses: WorkflowStatus[] }>(`/api/projects/${key}/workflow/statuses`, {
      method: "PUT",
      body: JSON.stringify({ statuses })
    }),
  board: (key: string) => request<BoardPayload>(`/api/projects/${key}/board`),
  issues: (key: string, params = "") => request<{ issues: Issue[] }>(`/api/projects/${key}/issues${params}`),
  createIssue: (key: string, payload: Partial<Issue>) =>
    request<{ issue: Issue }>(`/api/projects/${key}/issues`, {
      method: "POST",
      body: JSON.stringify(payload)
    }),
  issue: (id: string) =>
    request<{
      issue: Issue;
      comments: Comment[];
      history: IssueHistory[];
      development: Array<Record<string, unknown>>;
      attachments: Array<Record<string, unknown>>;
    }>(`/api/issues/${id}`),
  updateIssue: (id: string, payload: Partial<Issue>) =>
    request<{ issue: Issue }>(`/api/issues/${id}`, { method: "PATCH", body: JSON.stringify(payload) }),
  transitionIssue: (id: string, statusId: string) =>
    request<{ issue: Issue }>(`/api/issues/${id}/transition`, {
      method: "POST",
      body: JSON.stringify({ statusId })
    }),
  comment: (id: string, body: string) =>
    request<{ comment: Comment }>(`/api/issues/${id}/comments`, {
      method: "POST",
      body: JSON.stringify({ body })
    }),
  sprints: (key: string) => request<{ sprints: Sprint[] }>(`/api/projects/${key}/sprints`),
  createSprint: (key: string, payload: Partial<Sprint>) =>
    request<{ sprint: Sprint }>(`/api/projects/${key}/sprints`, {
      method: "POST",
      body: JSON.stringify(payload)
    }),
  startSprint: (key: string, id: string) =>
    request<{ sprint: Sprint }>(`/api/projects/${key}/sprints/${id}/start`, { method: "POST" }),
  completeSprint: (key: string, id: string) =>
    request<{ sprint: Sprint }>(`/api/projects/${key}/sprints/${id}/complete`, {
      method: "POST",
      body: JSON.stringify({})
    }),
  roadmap: (key: string) => request<{ epics: Array<Record<string, unknown>>; dependencies: Array<Record<string, unknown>> }>(`/api/projects/${key}/roadmap`),
  report: (key: string, type: string) => request<Record<string, unknown>>(`/api/projects/${key}/reports/${type}`),
  fields: (key: string) => request<{ fields: CustomFieldDefinition[] }>(`/api/projects/${key}/custom-fields`),
  createField: (key: string, payload: Partial<CustomFieldDefinition>) =>
    request<{ field: CustomFieldDefinition }>(`/api/projects/${key}/custom-fields`, {
      method: "POST",
      body: JSON.stringify(payload)
    }),
  automations: (key: string) => request<{ automations: AutomationRule[] }>(`/api/projects/${key}/automations`),
  createAutomation: (key: string, payload: Partial<AutomationRule>) =>
    request<{ automation: AutomationRule }>(`/api/projects/${key}/automations`, {
      method: "POST",
      body: JSON.stringify(payload)
    }),
  webhooks: (key: string) => request<{ webhooks: Array<Record<string, unknown>>; deliveries: Array<Record<string, unknown>> }>(`/api/projects/${key}/webhooks`),
  createWebhook: (key: string, payload: Record<string, unknown>) =>
    request<{ webhook: Record<string, unknown> }>(`/api/projects/${key}/webhooks`, {
      method: "POST",
      body: JSON.stringify(payload)
    })
};

async function refresh(): Promise<AuthState | null> {
  const result = await request<AuthState>("/api/auth/refresh", { method: "POST" }, false);
  setAccessToken(result.accessToken);
  return result;
}
